/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";

import { LocaleKeys } from "../../../model/locale";
import { localizedErrorFallback } from "../../../model/utility";
import type { IpAutoLoginError, SignInState } from "../signIn.types";
import {
  AUTH_ORCHESTRATION_COMPLETED,
  AUTH_ORCHESTRATION_FAILED,
  AUTH_ORCHESTRATION_IDLE,
  AUTH_ORCHESTRATION_IP_AUTO_LOGIN,
  AUTH_ORCHESTRATION_RESOLVING,
  IP_AUTO_LOGIN_STATUS_CLAIMING,
  IP_AUTO_LOGIN_STATUS_COMPLETED,
  IP_AUTO_LOGIN_STATUS_ERROR,
  IP_AUTO_LOGIN_STATUS_IDLE,
  IPL_CANCELLED,
  IPL_DEVICE_IDENTITY_RESET
} from "../signIn.types";
import {
  markAuthTypeAttempted,
  setAuthOrchestrationStatus,
  setIpAutoLoginStatus
} from "../signIn.actions";
import { SIGN_IN_FALLBACK_ERROR_CODE } from "../common/authErrors";
import { startIpAutoLoginSignIn } from "../thunks/ipAutoLogin.thunks";

/**
 * Case reducers owned by the orchestration walk that are dispatched from
 * the UI rather than from a thunk.
 */
export const ipAutoLoginReducers = {
  resetAuthOrchestration(state: SignInState): void {
    state.authOrchestrationStatus = AUTH_ORCHESTRATION_IDLE;
    state.attemptedAuthTypes = [];
    state.ipAutoLoginStatus = IP_AUTO_LOGIN_STATUS_IDLE;
    state.ipAutoLoginError = null;
    state.resolvedAuthType = "";
  }
};

/**
 * Applies the outcome of a failed silent attempt: record it, then either
 * hand back to the orchestration walk (fallback-eligible) or fail the
 * whole sign-in.
 */
function applyIpAutoLoginFailure(
  state: SignInState,
  ipError: IpAutoLoginError
): void {
  state.ipAutoLoginError = ipError;
  state.ipAutoLoginStatus = IP_AUTO_LOGIN_STATUS_ERROR;

  if (
    ipError.reason === IPL_CANCELLED &&
    state.authOrchestrationStatus === AUTH_ORCHESTRATION_COMPLETED
  ) {
    // Aborted because another flow already signed the device in —
    // leave the completed orchestration status untouched. Any other
    // cancellation must still fall through to the fallback walk,
    // otherwise the app stays parked on the splash screen forever.
    return;
  }

  // Mark the type attempted — except after a device identity reset, where
  // the point is to run IP auto-login again with the new identity. The thunk
  // allows that retry once per session, so this cannot loop.
  if (
    ipError.reason !== IPL_DEVICE_IDENTITY_RESET &&
    state.attemptedAuthTypes.indexOf(
      AuthenticationTypeV3.TivoIpAutoLoginIdp
    ) === -1
  ) {
    state.attemptedAuthTypes.push(AuthenticationTypeV3.TivoIpAutoLoginIdp);
  }

  if (ipError.fallbackEligible) {
    // The resolveNextAuthStep thunk will be re-dispatched by the
    // view layer (AppInitializer) or via middleware; set status to
    // trigger re-resolution.
    state.authOrchestrationStatus = AUTH_ORCHESTRATION_RESOLVING;
  } else {
    state.authOrchestrationStatus = AUTH_ORCHESTRATION_FAILED;
    state.signInError = ipError.message;
    state.signInErrorCode = ipError.code;
    // Every IP auto-login step wraps its failure into a mapped service error,
    // which keeps no response — so there is no body code to show, and the
    // alert falls back to the catalog code above.
    state.signInErrorServiceCode = "";
  }
}

/**
 * Reducers for the silent IP auto-login flow and the orchestration status
 * the flow reports as it walks the auth configurations.
 */
export function registerIpAutoLoginReducers(
  builder: ActionReducerMapBuilder<SignInState>
): void {
  builder.addCase(setAuthOrchestrationStatus, (state, action) => {
    state.authOrchestrationStatus = action.payload;
  });
  builder.addCase(setIpAutoLoginStatus, (state, action) => {
    state.ipAutoLoginStatus = action.payload;
  });
  builder.addCase(markAuthTypeAttempted, (state, action) => {
    if (state.attemptedAuthTypes.indexOf(action.payload) === -1) {
      state.attemptedAuthTypes.push(action.payload);
    }
  });
  builder.addCase(startIpAutoLoginSignIn.pending, (state) => {
    state.ipAutoLoginStatus = IP_AUTO_LOGIN_STATUS_CLAIMING;
    state.ipAutoLoginError = null;
    state.authOrchestrationStatus = AUTH_ORCHESTRATION_IP_AUTO_LOGIN;
  });
  builder.addCase(startIpAutoLoginSignIn.fulfilled, (state, action) => {
    state.domainToken = action.payload.domainToken;
    state.provisionResponse = action.payload.provisionResponse;
    state.resolvedAuthType = AuthenticationTypeV3.TivoIpAutoLoginIdp;
    state.ipAutoLoginStatus = IP_AUTO_LOGIN_STATUS_COMPLETED;
    state.authOrchestrationStatus = AUTH_ORCHESTRATION_COMPLETED;
    state.attemptedAuthTypes.push(AuthenticationTypeV3.TivoIpAutoLoginIdp);
  });
  builder.addCase(startIpAutoLoginSignIn.rejected, (state, action) => {
    const ipError: IpAutoLoginError | undefined = action.payload;
    if (ipError !== undefined) {
      applyIpAutoLoginFailure(state, ipError);
    } else {
      state.ipAutoLoginStatus = IP_AUTO_LOGIN_STATUS_ERROR;
      state.authOrchestrationStatus = AUTH_ORCHESTRATION_FAILED;
      state.signInError = localizedErrorFallback(
        LocaleKeys.SIGN_IN_AUTO_LOGIN_ERROR_MESSAGE
      );
      state.signInErrorCode = SIGN_IN_FALLBACK_ERROR_CODE;
      state.signInErrorServiceCode = "";
    }
  });
}
